/* Password-derived wrapping of the private RSA JWK.
   - PBKDF2-SHA256 over the password + per-user salt → AES-256-GCM key
   - AES-GCM-encrypts the JSON-encoded private JWK
   - { salt, iv, ciphertext } is uploaded; server never sees the password or private key.
*/

const PBKDF2_ITERS = 250_000;

function bytesToB64(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function b64ToBytes(b64) {
  const s = atob(b64);
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
  return bytes;
}

async function deriveKey(password, saltBytes) {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: saltBytes,
      iterations: PBKDF2_ITERS,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

/**
 * Wrap the private JWK with a password.
 * Returns { salt, iv, ciphertext } (all base64).
 */
export async function wrapPrivateKey(privateJwk, password) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(password, salt);
  const plaintext = new TextEncoder().encode(JSON.stringify(privateJwk));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext);
  return {
    salt: bytesToB64(salt.buffer),
    iv: bytesToB64(iv.buffer),
    ciphertext: bytesToB64(ct),
  };
}

/**
 * Unwrap with password. Throws on wrong password.
 */
export async function unwrapPrivateKey(wrapped, password) {
  const salt = b64ToBytes(wrapped.salt);
  const iv = b64ToBytes(wrapped.iv);
  const ct = b64ToBytes(wrapped.ciphertext);
  const key = await deriveKey(password, salt);
  const plain = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    ct
  );
  return JSON.parse(new TextDecoder().decode(plain));
}
